import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  ScrollText,
  Search,
  SearchX,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useAuditLog } from "@/hooks/useAdminUsers";
import type { AuditFilters, AuditRow } from "@/api/adminCenter";

/**
 * D-044 S4 — #8 Audit Log (VISUAL v1.1: read-only журнал CSecurityAudit;
 * рядок P8 actor/action/obj/old→new/sourceScreen + object-link; MP3 останні
 * 100 / 30 днів + фільтри; MP4 diff INLINE expand; MP7 object-link
 * permission-safe — НІКОЛИ 404; MP8 redaction server-side). AC-072: жодних
 * edit/delete.
 */

const PAGE_SIZE = 25;

export function AdminAuditPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;

  const [q, setQ] = useState("");
  const [actorId, setActorId] = useState("");
  const [action, setAction] = useState("");
  const [screen, setScreen] = useState("");
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(0);
  const [openDiff, setOpenDiff] = useState<Record<string, boolean>>({});

  const filters: AuditFilters = useMemo(
    () => ({
      q: q.trim() || undefined,
      actorId: actorId || undefined,
      action: action || undefined,
      sourceScreen: screen || undefined,
      days,
    }),
    [q, actorId, action, screen, days],
  );
  const auditQ = useAuditLog(filters, canView);

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
          <p className="mt-1 text-xs text-muted-foreground">Розділ доступний лише адміністраторам.</p>
        </div>
      </div>
    );
  }

  const data = auditQ.data;
  const items = data?.items ?? [];
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const hasFilter = !!(filters.q || filters.actorId || filters.action || filters.sourceScreen) || days !== 30;

  const resetFilters = () => {
    setQ("");
    setActorId("");
    setAction("");
    setScreen("");
    setDays(30);
    setPage(0);
  };

  const selCls = "rounded-md border border-input bg-background px-2 py-1.5 text-xs";

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1100px] space-y-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <ScrollText className="h-5 w-5" /> Журнал аудиту
          </h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            CSecurityAudit · read-only · останні {data?.limit ?? 100} подій за {days} дн.
          </p>
        </div>

        <div className="rounded-card border bg-card p-4">
          {/* MP3: фільтри */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(0);
                }}
                placeholder="Пошук…"
                className="h-8 w-[180px] pl-7 text-xs"
              />
            </div>
            <select
              value={actorId}
              onChange={(e) => {
                setActorId(e.target.value);
                setPage(0);
              }}
              className={selCls}
              aria-label="Хто"
            >
              <option value="">Хто: всі</option>
              {data?.options.actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <select
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(0);
              }}
              className={selCls}
              aria-label="Дія"
            >
              <option value="">Дія: всі</option>
              {data?.options.actions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <select
              value={screen}
              onChange={(e) => {
                setScreen(e.target.value);
                setPage(0);
              }}
              className={selCls}
              aria-label="Екран"
            >
              <option value="">Екран: всі</option>
              {data?.options.screens.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              value={days}
              onChange={(e) => {
                setDays(Number(e.target.value));
                setPage(0);
              }}
              className={selCls}
              aria-label="Період"
            >
              <option value={1}>Сьогодні</option>
              <option value={7}>7 днів</option>
              <option value={30}>30 днів</option>
            </select>
            {hasFilter && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <RotateCcw className="mr-1 h-3.5 w-3.5" /> Скинути
              </Button>
            )}
          </div>

          {auditQ.isLoading && (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          )}
          {auditQ.isError && (
            <div className="p-4 text-sm text-destructive">Не вдалося завантажити журнал.</div>
          )}

          {data && items.length === 0 && (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              <SearchX className="mx-auto mb-2 h-5 w-5" />
              {hasFilter ? "За фільтром нічого не знайдено" : "Подій у вікні немає"}
              {hasFilter && (
                <div className="mt-3">
                  <Button variant="outline" size="sm" onClick={resetFilters}>
                    <RotateCcw className="mr-1 h-3.5 w-3.5" /> Скинути фільтр
                  </Button>
                </div>
              )}
            </div>
          )}

          {data && items.length > 0 && (
            <>
              <table className="w-full table-fixed border-collapse text-sm">
                <colgroup>
                  <col className="w-[13%]" />
                  <col className="w-[15%]" />
                  <col className="w-[19%]" />
                  <col className="w-[28%]" />
                  <col className="w-[12%]" />
                  <col className="w-[13%]" />
                </colgroup>
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-2 py-2 font-medium">Час</th>
                    <th className="px-2 py-2 font-medium">Хто</th>
                    <th className="px-2 py-2 font-medium">Дія</th>
                    <th className="px-2 py-2 font-medium">Обʼєкт</th>
                    <th className="px-2 py-2 font-medium">Зміни</th>
                    <th className="px-2 py-2 font-medium">Екран</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((r) => (
                    <AuditRowView
                      key={r.id}
                      row={r}
                      diffOpen={!!openDiff[r.id]}
                      onToggleDiff={() => setOpenDiff((s) => ({ ...s, [r.id]: !s[r.id] }))}
                    />
                  ))}
                </tbody>
              </table>

              <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Показано {page * PAGE_SIZE + 1}–{Math.min(items.length, (page + 1) * PAGE_SIZE)} з{" "}
                  {items.length} (останні {data.limit} · {days} дн.)
                </span>
                <span className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    <ChevronLeft className="h-3.5 w-3.5" /> Назад
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= pages - 1}
                    onClick={() => setPage(page + 1)}
                  >
                    Далі <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditRowView({
  row: r,
  diffOpen,
  onToggleDiff,
}: {
  row: AuditRow;
  diffOpen: boolean;
  onToggleDiff: () => void;
}) {
  const hasDiff = r.oldValue !== null || r.newValue !== null;
  return (
    <>
      <tr className="border-t align-top">
        <td className="px-2 py-2 text-xs tabular-nums text-muted-foreground">{r.occurredAt}</td>
        <td className="truncate px-2 py-2 text-xs">{r.actor.name}</td>
        <td className="px-2 py-2">
          <span className="rounded-badge bg-info px-2 py-0.5 text-[11px] text-info-foreground">{r.action}</span>
        </td>
        <td className="px-2 py-2 text-xs">
          <ObjectCell row={r} />
        </td>
        <td className="px-2 py-2">
          {hasDiff ? (
            // MP4: diff inline expand (не drawer)
            <button
              type="button"
              onClick={onToggleDiff}
              aria-expanded={diffOpen}
              className="inline-flex items-center gap-0.5 text-xs text-primary underline-offset-2 hover:underline"
            >
              {diffOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              diff
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
        <td className="px-2 py-2">
          {r.sourceScreen ? (
            <span className="rounded-badge bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
              {r.sourceScreen}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
      </tr>
      {hasDiff && diffOpen && (
        <tr>
          <td colSpan={6} className="px-2 pb-2">
            <div className="rounded-md bg-muted/40 px-3 py-2 font-mono text-[11px] leading-relaxed">
              {r.oldValue !== null && (
                <div>
                  <span className="text-muted-foreground">old:</span> {r.oldValue}
                </div>
              )}
              {r.newValue !== null && (
                <div>
                  <span className="text-muted-foreground">new:</span> {r.newValue}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/** MP7: accessible→Link · deleted→snapshot · sensitive→disabled з причиною · none→текст. НІКОЛИ 404. */
function ObjectCell({ row: r }: { row: AuditRow }) {
  const label = `${r.objectType} · ${r.objectLink.name ?? r.objectId}`;
  switch (r.objectLink.state) {
    case "accessible":
      return (
        <Link to={r.objectLink.route!} className="text-primary underline-offset-2 hover:underline">
          {label}
        </Link>
      );
    case "deleted":
      return (
        <span className="text-muted-foreground" title="Обʼєкт видалено — показано знімок">
          {label} <span className="text-[10px]">(видалено)</span>
        </span>
      );
    case "sensitive":
      return (
        <span
          className={cn("cursor-not-allowed text-muted-foreground")}
          title={r.objectLink.reason ?? "Перехід вимкнено"}
        >
          {label} 🔒
        </span>
      );
    default:
      return <span>{label}</span>;
  }
}
